import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { IMG } from "@/lib/images";
import PageBanner from "@/components/common/PageBanner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill name, email and a short message."); return; }
    setSubmitting(true);
    try {
      await api.post("/public/leads", { ...form, source: "contact_form" });
      toast.success("Thank you. We will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  }

  return (
    <div data-testid="contact-page" className="bg-[var(--bone)]">
      <PageBanner
        eyebrow="Contact"
        title="Let&apos;s plan something"
        italicWord="cinematic."
        kicker="A brief, a budget, or a question — our strategists reply within 24 hours."
        image={IMG.mumbai}
      />
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <form onSubmit={submit} className="lg:col-span-7 surface p-8 lg:p-12 space-y-5" data-testid="contact-form">
          <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[var(--gold)]">Write to us</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Name *"><input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Email *"><input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Phone"><input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Company"><input data-testid="contact-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></Field>
          </div>
          <Field label="How can we help *">
            <textarea data-testid="contact-message" rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your brand, target cities, budget and timing…" />
          </Field>
          <button type="submit" data-testid="contact-submit" disabled={submitting} className="btn-primary">
            {submitting ? "Sending…" : "Send message"}
          </button>
        </form>
        <aside className="lg:col-span-5 space-y-4">
          <ContactItem icon={Mail} title="Email" body="partners@fundle.in" />
          <ContactItem icon={Phone} title="Phone" body="+91 22 0000 0000 · Mon–Sat" />
          <ContactItem icon={MapPin} title="HQ" body="Mumbai · Delhi · Bengaluru" />
          <ContactItem icon={Sparkles} title="Try the AI Planner" body="Get a media plan in 90 seconds." href="/ai-planner" />
        </aside>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--slate-2)] mb-2">{label}</div>
      {children}
    </label>
  );
}

function ContactItem({ icon: Icon, title, body, href }) {
  const inner = (
    <>
      <Icon size={20} className="text-[var(--gold)] mt-1" strokeWidth={1.5} />
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--slate-2)]">{title}</div>
        <div className="text-[var(--ink)] mt-1 font-medium">{body}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} className="surface surface-hover p-6 flex items-start gap-4">{inner}</a>
  ) : (
    <div className="surface p-6 flex items-start gap-4">{inner}</div>
  );
}
